// IMP-455: the white-label assistant identity. A partner names the assistant
// and picks one of these offered brand colors; every client account under the
// partner renders the assistant from that configuration. Theme ids are
// validated server-side against PartnerAccount::ASSISTANT_COLORS — keep the
// two lists in sync.
//
// IMP-464: this is the single shared palette — partner onboarding's Identity
// step and Partner Settings → Identity both render it, in this order. Colors
// run in spectrum order (red → rose) with the near-neutral slate last.

export const DEFAULT_ASSISTANT_NAME = "AI Assistant"

export interface AssistantTheme {
  id: string
  swatch: string
  // The orb blends three hues (see SiriOrb).
  colors: { c1: string; c2: string; c3: string }
}

export const ASSISTANT_THEMES: AssistantTheme[] = [
  {
    id: "red",
    swatch: "#DC2626",
    colors: {
      c1: "oklch(62% 0.21 25)",
      c2: "oklch(80% 0.1 20)",
      c3: "oklch(70% 0.17 40)",
    },
  },
  {
    id: "amber",
    swatch: "#D97706",
    colors: {
      c1: "oklch(72% 0.15 70)",
      c2: "oklch(86% 0.1 85)",
      c3: "oklch(76% 0.14 55)",
    },
  },
  {
    id: "green",
    swatch: "#16A34A",
    colors: {
      c1: "oklch(64% 0.17 150)",
      c2: "oklch(84% 0.1 155)",
      c3: "oklch(72% 0.15 140)",
    },
  },
  {
    id: "teal",
    swatch: "#0D9488",
    colors: {
      c1: "oklch(68% 0.13 190)",
      c2: "oklch(84% 0.09 200)",
      c3: "oklch(74% 0.13 175)",
    },
  },
  {
    id: "cyan",
    swatch: "#0891B2",
    colors: {
      c1: "oklch(66% 0.13 220)",
      c2: "oklch(84% 0.08 210)",
      c3: "oklch(72% 0.12 230)",
    },
  },
  {
    id: "blue",
    swatch: "#2563EB",
    colors: {
      c1: "oklch(66% 0.19 255)",
      c2: "oklch(82% 0.1 220)",
      c3: "oklch(72% 0.16 275)",
    },
  },
  {
    id: "violet",
    swatch: "#7C3AED",
    colors: {
      c1: "oklch(62% 0.2 295)",
      c2: "oklch(80% 0.11 280)",
      c3: "oklch(70% 0.17 310)",
    },
  },
  {
    id: "magenta",
    swatch: "#C026D3",
    colors: {
      c1: "oklch(64% 0.22 330)",
      c2: "oklch(82% 0.11 325)",
      c3: "oklch(70% 0.18 345)",
    },
  },
  {
    id: "rose",
    swatch: "#B23A67",
    colors: {
      c1: "oklch(62% 0.18 5)",
      c2: "oklch(82% 0.1 350)",
      c3: "oklch(70% 0.16 20)",
    },
  },
  // Near-neutral, deliberately last — an out-of-spectrum "no strong brand
  // color" option.
  {
    id: "slate",
    swatch: "#475569",
    colors: {
      c1: "oklch(60% 0.04 250)",
      c2: "oklch(80% 0.02 240)",
      c3: "oklch(70% 0.04 260)",
    },
  },
]

const DEFAULT_THEME_ID = "blue"

// Unknown/unset ids fall back to the standard blue.
export function assistantTheme(id: string | null | undefined): AssistantTheme {
  return (
    ASSISTANT_THEMES.find((t) => t.id === id) ??
    ASSISTANT_THEMES.find((t) => t.id === DEFAULT_THEME_ID)!
  )
}

// IMP-464: the names the onboarding dice deals from. The bar is tighter than
// it looks: the voice agent speaks this name aloud on every call — one or two
// syllables, gender-neutral, unambiguous when heard, no collision with
// existing assistants (Alexa, Siri, Ava, Cortana).
export const ASSISTANT_NAMES = [
  "Ada",
  "Aria",
  "Arlo",
  "Bex",
  "Bodhi",
  "Cass",
  "Cleo",
  "Dane",
  "Echo",
  "Eli",
  "Ember",
  "Emery",
  "Ever",
  "Ezra",
  "Fable",
  "Finley",
  "Frankie",
  "Hollis",
  "Ira",
  "Iza",
  "Ivo",
  "Jess",
  "Jordan",
  "Juno",
  "Kai",
  "Kit",
  "Lennox",
  "Linden",
  "Lux",
  "Marlo",
  "Mika",
  "Milo",
  "Nico",
  "Noa",
  "Nova",
  "Odin",
  "Orion",
  "Quinn",
  "Reese",
  "Remy",
  "Rio",
  "Riven",
  "Rowan",
  "Sage",
  "Sasha",
  "Shiloh",
  "Sol",
  "Tate",
  "Wilder",
]

// A random assistant name, never the one currently in the field (the dice
// must always visibly reroll).
export function rollAssistantName(exclude?: string | null): string {
  const pool = ASSISTANT_NAMES.filter((n) => n !== exclude)
  return pool[Math.floor(Math.random() * pool.length)]
}

// IMP-464: map a detected brand color (e.g. the dominant color of the
// partner's logo) onto the offered palette. Near-neutrals map to slate;
// chromatic colors map to the nearest-hue chromatic theme when reasonably
// close; anything else returns null and the caller falls back to blue.
export function themeForBrandColor(
  hex: string | null | undefined,
): string | null {
  const rgb = parseHex(hex)
  if (!rgb) return null

  const { h, s } = rgbToHsl(rgb)
  if (s < 0.12) return "slate"

  let best: { id: string; distance: number } | null = null
  for (const theme of ASSISTANT_THEMES) {
    if (theme.id === "slate") continue
    const swatch = rgbToHsl(parseHex(theme.swatch)!)
    const distance = hueDistance(h, swatch.h)
    if (!best || distance < best.distance) best = { id: theme.id, distance }
  }
  return best && best.distance <= 40 ? best.id : null
}

function parseHex(
  hex: string | null | undefined,
): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{6})$/i.exec(hex?.trim() ?? "")
  if (!m) return null
  const n = parseInt(m[1], 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function rgbToHsl({ r, g, b }: { r: number; g: number; b: number }): {
  h: number
  s: number
} {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const delta = max - min
  const lightness = (max + min) / 2
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1)) || 0
  let h = 0
  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6
    else if (max === gn) h = (bn - rn) / delta + 2
    else h = (rn - gn) / delta + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s }
}

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}
